import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/base/Breadcrumb";
import useDocumentMeta from "@/hooks/useDocumentMeta";

const services = [
  {
    icon: "ri-book-open-line",
    title: "分集剧情解析",
    desc: "逐集梳理剧情脉络、人物动机与关键转折，帮助你看懂每一处埋下的伏笔。",
  },
  {
    icon: "ri-user-star-line",
    title: "角色档案",
    desc: "整理主要角色的背景、关系与行为逻辑，理解他们在家庭中的位置与选择。",
  },
  {
    icon: "ri-chat-smile-2-line",
    title: "观众点评",
    desc: "汇集影迷的真实观感与讨论，从不同视角重新审视这部剧的得与失。",
  },
  {
    icon: "ri-camera-lens-line",
    title: "镜头语言拆解",
    desc: "从调度、光影与构图等角度，解析作品在视听层面上的表达意图。",
  },
];

export default function About() {
  useDocumentMeta({
    title: "关于本站 - 美国式禁忌2影视内容介绍站",
    description:
      "了解《美国式禁忌2》影视内容站的定位、内容范围、联系方式与免责声明，我们专注于剧集介绍、剧情解析与角色资料。",
    keywords: "美国式禁忌2,关于本站,美国式禁忌2介绍,影视内容站",
  });

  return (
    <SiteLayout>
      <div className="max-w-[1200px] mx-auto px-4 md:px-8 pt-8 md:pt-12 pb-16">
        <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于本站" }]} />

        <section className="mt-6 rounded-lg overflow-hidden border border-background-200 bg-gradient-to-br from-primary-950 via-background-100 to-background-100 p-8 md:p-14">
          <p className="text-xs font-medium tracking-[0.2em] uppercase text-accent-400 mb-3">
            About Us
          </p>
          <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950 max-w-3xl leading-tight">
            专注《美国式禁忌2》的深度影视内容站
          </h1>
          <p className="mt-5 text-sm md:text-base text-foreground-700 leading-relaxed max-w-3xl">
            本站围绕电视剧《美国式禁忌2》构建内容，聚焦剧集介绍、分集剧情解析、角色资料与观众点评。
            我们相信，一部好的剧集值得被认真对待——它的每一处沉默、每一次对视、每一个被刻意藏起的细节，都值得被好好讨论。
            因此，这里没有浮夸的标题党，只有尽量严谨、尽量深度的内容整理。
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to="/category"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
            >
              浏览剧集列表
            </Link>
            <Link
              to="/feedback"
              className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 hover:border-accent-500 text-foreground-800 hover:text-accent-300 text-sm whitespace-nowrap cursor-pointer transition-colors"
            >
              联系编辑部
            </Link>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2 mb-3">
            <span className="w-1.5 h-6 rounded-full bg-primary-600" />
            品牌背景与内容范围
          </h2>
          <div className="space-y-4 text-sm md:text-[15px] text-foreground-700 leading-[1.9] max-w-4xl">
            <p>
              本站是一个由影迷自发维护的内容站点，面向喜欢美剧、尤其关注剧情细节与角色塑造的观众群体。
              我们的内容团队会持续跟进《美国式禁忌2》的更新节奏，在上线后第一时间整理剧情要点、角色动线与讨论热点。
            </p>
            <p>
              内容范围包括：剧集基础信息（首播日期、主创、演员、类型标签）、分集剧情简介与深度解析、
              角色档案与关系梳理、以及观众点评精选。所有内容均为文字与图文介绍，供学习与交流使用。
            </p>
          </div>
        </section>

        <section className="mt-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 flex items-center gap-2 mb-6">
            <span className="w-1.5 h-6 rounded-full bg-primary-600" />
            我们提供什么
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {services.map((service) => (
              <div
                key={service.title}
                className="rounded-lg bg-background-100 border border-background-200 p-5"
              >
                <span className="w-11 h-11 flex items-center justify-center rounded-md bg-background-200 text-accent-400 text-xl mb-4">
                  <i className={service.icon} />
                </span>
                <h3 className="font-heading text-base font-bold text-foreground-950">
                  {service.title}
                </h3>
                <p className="mt-2 text-sm text-foreground-600 leading-relaxed">
                  {service.desc}
                </p>
              </div>
            ))}
          </div>
        </section>

        <section className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2 mb-4">
              <i className="ri-mail-send-line text-accent-400" />
              联系方式
            </h2>
            <div className="space-y-3 text-sm text-foreground-700">
              <p className="flex items-center gap-2">
                <i className="ri-mail-line text-accent-400" />
                编辑部邮箱：contact@americantaboo2.example.com
              </p>
              <p className="flex items-center gap-2">
                <i className="ri-chat-3-line text-accent-400" />
                内容纠错、投稿与建议均可通过邮箱或反馈表单提交。
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-3">
              <a
                href="mailto:contact@americantaboo2.example.com"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-600 hover:bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer transition-colors"
              >
                <i className="ri-mail-open-line" />
                发送邮件
              </a>
              <Link
                to="/feedback"
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 hover:border-accent-500 text-foreground-800 hover:text-accent-300 text-sm whitespace-nowrap cursor-pointer transition-colors"
              >
                填写反馈表单
              </Link>
            </div>
          </div>

          <div id="disclaimer" className="rounded-lg bg-background-100 border border-background-200 p-6 md:p-8 scroll-mt-24">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 flex items-center gap-2 mb-4">
              <i className="ri-shield-check-line text-accent-400" />
              使用条款与免责声明
            </h2>
            <ul className="space-y-3 text-sm text-foreground-700 leading-relaxed">
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-accent-400 mt-0.5" />
                本站为影视作品介绍与观众讨论性质的内容站，<strong className="text-foreground-900">不提供任何影片资源、播放或下载链接</strong>。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-accent-400 mt-0.5" />
                站内剧情解析与评价仅代表内容整理者与观众的个人观点，不构成对作品的官方结论。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-accent-400 mt-0.5" />
                剧照与图片仅用于内容说明，版权归原版权方所有；如涉及侵权，请通过联系方式告知，我们将及时处理。
              </li>
              <li className="flex gap-2">
                <i className="ri-checkbox-circle-line text-accent-400 mt-0.5" />
                站内评论、点赞与搜索等功能为前端演示，评论内容不会上传至服务器，刷新后不保留。
              </li>
            </ul>
          </div>
        </section>
      </div>
    </SiteLayout>
  );
}